import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable';
import { Resizable } from 'react-resizable';
import { browserHistory } from 'react-router';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import MaximizeIcon from '@mui/icons-material/Maximize';
import { useDispatch, useSelector } from 'react-redux';

import DashboardHeader from '../DashboardModal/components/DashboardHeader';
import PopupAlert from '../Notifications/PopupAlert';
import ProjectNameLogoBlock from './blocks/ProjectNameLogoBlock';
import RequiredMaintenanceBlock from './blocks/RequiredMaintenanceBlock';
import ReportOverviewBlock from './blocks/ReportOverviewBlock';
import ReportSensorsTable from './blocks/ReportSensorsTable';
import SystemStatusBlock from './blocks/SystemStatusBlock';
import {
  normalizeMaintenanceDashboardData,
  normalizeMaintenanceReportSensors,
} from './maintenanceDashboardData';
import {
  fetchCustomerLogo,
  fetchMaintenanceDashboard,
  fetchMaintenanceReportSensors,
} from '../../actions/DashboardActions';
import {
  changeSensorsContext,
  selectSensor,
} from '../../actions/SensorsActions';
import { downloadSensorsMaintenanceReport } from '../../actions/ReportsActions';

import 'react-resizable/css/styles.css';
import '../DashboardModal/DashboardModal.scss';
import './MaintenanceDashboardModal.scss';

const getInitialWidth = () => Math.round(window.innerWidth * 0.65);
const getFixedColumnWidth = () => Math.round((getInitialWidth() * 3) / 12);
const DASHBOARD_MIN_WIDTH = 900;
const DASHBOARD_MIN_HEIGHT = 610;
const DASHBOARD_INITIAL_OPEN_HEIGHT = 800;
const DASHBOARD_MINIMIZED_INITIAL_WIDTH = 880;
const DASHBOARD_MINIMIZED_INITIAL_HEIGHT = 248;
const DASHBOARD_TOP_ROW_HEIGHT = 170;
const DASHBOARD_BOTTOM_ROW_HEIGHT = 340;
const DASHBOARD_GRID_ITEM_VERTICAL_PADDING = 12;
const DASHBOARD_VIEW_PADDING = 32;
const DASHBOARD_HEADER_HEIGHT = 48;

const getInitialHeight = () => {
  const fittedHeight =
    DASHBOARD_TOP_ROW_HEIGHT +
    DASHBOARD_GRID_ITEM_VERTICAL_PADDING +
    (DASHBOARD_BOTTOM_ROW_HEIGHT + DASHBOARD_GRID_ITEM_VERTICAL_PADDING) +
    DASHBOARD_VIEW_PADDING +
    DASHBOARD_HEADER_HEIGHT;

  return Math.min(
    window.innerHeight - 20,
    Math.max(fittedHeight, DASHBOARD_INITIAL_OPEN_HEIGHT)
  );
};

const MaintenanceDashboardModal = ({ open, onClose }, context) => {
  const dispatch = useDispatch();
  const selectedProject = useSelector(
    (state) => state.leaksList?.selectedProject
  );
  const projects = useSelector(
    (state) => state.leaksList?.projectsList?.items || []
  );
  const customers = useSelector((state) => state.leaksList?.customers);
  const projectSensors = useSelector(
    (state) => state.leaksList?.leaksByProject?.[selectedProject]?.sensors
  );
  const selectedSensorId = useSelector(
    (state) => state.leaksList?.leaksByProject?.selectedFeature
  );
  const projectFromLeaks = useSelector(
    (state) => state.leaksList?.leaksByProject?.[selectedProject]?.project
  );
  const customerLogo = useSelector(
    (state) => state.leaksList?.dashboard?.customerLogo
  );
  const maintenanceDashboard = useSelector(
    (state) => state.leaksList?.dashboard?.maintenanceDashboard
  );
  const maintenanceReportSensors = useSelector(
    (state) => state.leaksList?.dashboard?.maintenanceReportSensors
  );
  const timeZone = useSelector((state) => state.local?.timeZone);
  const currentLang = useSelector((state) => state.i18nState?.lang || 'en');
  const isHebrew = currentLang === 'he' || currentLang === 'hebrew';

  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [hasBeenResized, setHasBeenResized] = useState(false);
  const [reportSort, setReportSort] = useState({
    key: null,
    direction: 'asc',
  });
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [minimizedPosition, setMinimizedPosition] = useState(null);
  const [minimizedSize, setMinimizedSize] = useState({
    width: Math.min(DASHBOARD_MINIMIZED_INITIAL_WIDTH, window.innerWidth * 0.95),
    height: Math.min(DASHBOARD_MINIMIZED_INITIAL_HEIGHT, window.innerHeight * 0.8),
  });
  const [popupAlert, setPopupAlert] = useState({
    popupKey: -1,
    message: '',
    severity: 'info',
  });
  const [size, setSize] = useState({
    width: getInitialWidth(),
    height: getInitialHeight(),
  });
  const [position, setPosition] = useState({
    x: Math.max(0, (window.innerWidth - getInitialWidth()) / 2),
    y: 20,
  });
  const resizeOriginRef = useRef(null);
  const minimizedResizeOriginRef = useRef(null);
  const modalRef = useRef(null);

  const currentProject = useMemo(() => {
    const project = projects.find((item) => item.ID === selectedProject);
    if (project) return project;
    if (projectFromLeaks) {
      return {
        ID: selectedProject,
        ShortName:
          projectFromLeaks.ShortName ||
          projectFromLeaks.Name ||
          projectFromLeaks.name,
        ...projectFromLeaks,
      };
    }
    return selectedProject
      ? {
        ID: selectedProject,
        ShortName: `Project ${String(selectedProject).slice(0, 8)}...`,
      }
      : null;
  }, [projects, projectFromLeaks, selectedProject]);

  const currentCustomerId =
    currentProject?.CustomerID ||
    currentProject?.CustomerId ||
    currentProject?.customer_id;

  const { maintenanceData } = useMemo(
    () =>
      normalizeMaintenanceDashboardData(
        maintenanceDashboard?.data,
        currentProject?.ID
      ),
    [maintenanceDashboard, currentProject?.ID]
  );

  const sensorData = useMemo(() => {
    const total = projectSensors?.sensors?.length || 0;
    const inactive = Number(maintenanceData.inactive) || 0;
    return {
      total,
      active: Math.max(0, total - inactive),
      inactive,
    };
  }, [maintenanceData.inactive, projectSensors?.sensors]);

  const reportSensors = useMemo(() => {
    const normalizedSensors = normalizeMaintenanceReportSensors(
      maintenanceReportSensors?.data
    );

    return normalizedSensors.map((sensor) => {
      const sensorId =
        sensor?.id || sensor?.ID || sensor?.SensorID || sensor?.sensor_id;
      const existingSensor =
        sensorId && projectSensors?.sensors && projectSensors?.indexMap
          ? projectSensors.sensors[projectSensors.indexMap[sensorId]]
          : null;

      return {
        ...existingSensor,
        ...sensor,
        id: existingSensor?.id || sensorId,
        SensorID:
          existingSensor?.SensorID ||
          sensor?.SensorID ||
          sensor?.sensor_id ||
          sensorId,
        DeviceID:
          sensor?.DeviceID || sensor?.device_id || existingSensor?.DeviceID,
      };
    });
  }, [
    maintenanceReportSensors?.data,
    projectSensors?.sensors,
    projectSensors?.indexMap,
  ]);

  const requiredMaintenanceItems = useMemo(() => {
    const items = [
      {
        label: context.t('sensor_replacement') || 'Sensor Replacement',
        count: Number(maintenanceData.sensors_replace) || 0,
      },
      {
        label: context.t('tail_replacement') || 'Tail Replacement',
        count: Number(maintenanceData.audio_problems) || 0,
      },
      {
        label: context.t('to_check') || 'Need To Check',
        count: Number(maintenanceData.to_check) || 0,
      },
    ];

    const maxCount = Math.max(...items.map(({ count }) => count), 0);

    return items.map((item) => ({
      ...item,
      ratio: maxCount > 0 ? item.count / maxCount : 0,
    }));
  }, [
    currentLang,
    maintenanceData.audio_problems,
    maintenanceData.sensors_replace,
    maintenanceData.to_check,
  ]);

  const totalFaultyDevices = Number(maintenanceData.faulty) || 0;

  useEffect(() => {
    if (!open || !currentProject?.ID) return;

    // dispatch(fetchMaintenanceDashboard(currentProject.ID));
    dispatch(fetchMaintenanceReportSensors(currentProject.ID));

    const customerIndex = customers?.indexMap?.[currentCustomerId];
    const customer =
      customerIndex !== undefined ? customers?.items?.[customerIndex] : null;

    if (currentCustomerId && customer?.S3Bucket && customer?.Logo) {
      dispatch(fetchCustomerLogo(currentCustomerId));
    }
  }, [
    open,
    currentProject?.ID,
    currentCustomerId,
    customers?.items,
    customers?.indexMap,
    dispatch,
  ]);

  useEffect(() => {
    if (!open) return undefined;

    const resetPanel = () => {
      if (isMaximized || isMinimized) return;
      const width = getInitialWidth();
      setHasBeenResized(false);
      setSize({ width, height: getInitialHeight() });
      setPosition({ x: Math.max(0, (window.innerWidth - width) / 2), y: 20 });
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    resetPanel();
    window.addEventListener('resize', resetPanel);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', resetPanel);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, isMaximized, isMinimized, onClose]);

  useEffect(() => {
    if (!open) {
      setPopupAlert((currentAlert) =>
        currentAlert.popupKey === -1
          ? currentAlert
          : {
            popupKey: -1,
            message: '',
            severity: 'info',
          }
      );
      return;
    }

    setReportSort({ key: null, direction: 'asc' });
    setIsDownloadingReport(false);
  }, [open]);

  if (!open) return null;

  const toggleMinimize = () => {
    setIsMinimized((value) => !value);
    setIsMaximized(false);
  };

  const toggleMaximize = () => setIsMaximized((value) => !value);

  const startResize = () => {
    const measuredSize = {
      width: size.width,
      height: modalRef.current?.offsetHeight || size.height,
    };

    resizeOriginRef.current = {
      position,
      size: measuredSize,
    };
    setSize(measuredSize);
    setHasBeenResized(true);
  };

  const resizeWindow = (_, { size: nextSize, handle }) => {
    const origin = resizeOriginRef.current || { position, size };
    const resizesFromLeft = handle.includes('w');
    const resizesFromTop = handle.includes('n');
    const resizesToRight = handle.includes('e');
    const resizesToBottom = handle.includes('s');

    const maxWidth = resizesFromLeft
      ? origin.position.x + origin.size.width - 10
      : resizesToRight
        ? window.innerWidth - origin.position.x - 10
        : origin.size.width;
    const maxHeight = resizesFromTop
      ? origin.position.y + origin.size.height - 10
      : resizesToBottom
        ? window.innerHeight - origin.position.y - 10
        : origin.size.height;
    const constrainedSize = {
      width: Math.min(nextSize.width, maxWidth),
      height: Math.min(nextSize.height, maxHeight),
    };

    setSize(constrainedSize);
    setPosition({
      x: resizesFromLeft
        ? origin.position.x + origin.size.width - constrainedSize.width
        : origin.position.x,
      y: resizesFromTop
        ? origin.position.y + origin.size.height - constrainedSize.height
        : origin.position.y,
    });
  };

  const stopResize = () => {
    resizeOriginRef.current = null;
  };

  const notify = (message, severity) => {
    setPopupAlert({
      popupKey: Date.now(),
      message,
      severity,
    });
  };

  const sortReportBy = (key) => {
    setReportSort((currentSort) => ({
      key,
      direction:
        currentSort.key === key && currentSort.direction === 'asc'
          ? 'desc'
          : 'asc',
    }));
  };

  const downloadMaintenanceReport = () => {
    setIsDownloadingReport(true);

    dispatch(
      downloadSensorsMaintenanceReport((error, hasData) => {
        setIsDownloadingReport(false);

        if (error) {
          notify(
            error.message ||
            context.t('failed_to_download_report') ||
            'Failed to download report',
            'error'
          );
          return;
        }

        if (!hasData) {
          notify(
            context.t('no_data_available_for_maintenance_report') ||
            'No data available for the Maintenance report',
            'warning'
          );
        }
      })
    );
  };

  const handleReportSensorClick = (sensor) => {
    const sensorId =
      sensor?.id || sensor?.ID || sensor?.SensorID || sensor?.sensor_id;
    const existingSensor =
      sensorId && projectSensors?.sensors && projectSensors?.indexMap
        ? projectSensors.sensors[projectSensors.indexMap[sensorId]]
        : null;
    const selectedSensor = {
      ...sensor,
      ...existingSensor,
      id: sensorId,
      SensorID: sensor?.SensorID || sensor?.sensor_id || sensorId,
      DeviceID:
        sensor?.DeviceID || sensor?.device_id || existingSensor?.DeviceID,
    };

    if (!isMinimized) {
      setIsMinimized(true);
      setIsMaximized(false);
    }

    if (sensorId) {
      browserHistory.push('/sensors');
      dispatch(changeSensorsContext('LeaksSensors'));
      dispatch(selectSensor(selectedSensor));
      window.dispatchEvent(
        new CustomEvent('focusSensor', {
          detail: {
            sensorId,
            sensor: selectedSensor,
            reportType: 'maintenance',
            keepDashboardOpen: true,
          },
        })
      );
    }
  };

  if (isMinimized) {
    const defaultPosition = minimizedPosition || {
      x: Math.max(10, window.innerWidth * 0.45),
      y: 100,
    };

    const startMinimizedResize = () => {
      minimizedResizeOriginRef.current = {
        position: defaultPosition,
        size: minimizedSize,
      };
    };

    const resizeMinimizedWindow = (_, { size: nextSize, handle }) => {
      const origin = minimizedResizeOriginRef.current || {
        position: defaultPosition,
        size: minimizedSize,
      };

      setMinimizedSize({
        width: nextSize.width,
        height: nextSize.height,
      });

      if (handle === 'sw') {
        const nextX = origin.position.x + (origin.size.width - nextSize.width);
        setMinimizedPosition({
          x: Math.max(0, nextX),
          y: origin.position.y,
        });
      }
    };

    return (
      <Draggable
        handle='.dashboard-minimized-header'
        position={defaultPosition}
        onStop={(_, data) => setMinimizedPosition({ x: data.x, y: data.y })}
      >
        <div className='dashboard-modal-minimized-container'>
          <Resizable
            width={minimizedSize.width}
            height={minimizedSize.height}
            onResizeStart={startMinimizedResize}
            onResize={resizeMinimizedWindow}
            minConstraints={[500, 180]}
            maxConstraints={[
              Math.round(window.innerWidth * 0.95),
              Math.round(window.innerHeight * 0.8),
            ]}
            resizeHandles={['sw', 'se']}
          >
            <Paper
              className='dashboard-modal-minimized maintenance-dashboard-minimized'
              dir='ltr'
              style={{
                width: minimizedSize.width,
                height: minimizedSize.height,
              }}
            >
              <div className='dashboard-minimized-header'>
                <span className='title'>
                  {(context.t('maintenance_dashboard') ||
                    'Maintenance Dashboard') +
                    ' — ' +
                    (context.t('maintenance_report') || 'Maintenance Report')}
                </span>
                <div className='header-actions'>
                  <IconButton size='small' onClick={toggleMinimize}>
                    <MaximizeIcon fontSize='small' />
                  </IconButton>
                  <IconButton size='small' onClick={onClose}>
                    <CloseIcon fontSize='small' />
                  </IconButton>
                </div>
              </div>
              <Box className='maintenance-minimized-body'>
                <ReportSensorsTable
                  className='maintenance-minimized-report-table'
                  useNativeTable
                  reportSensors={reportSensors}
                  onSensorClick={handleReportSensorClick}
                  emptyText={
                    context.t('no_sensors_found_for_maintenance_report') ||
                    'No sensors found for the Maintenance report.'
                  }
                  selectedSensorId={selectedSensorId}
                  sortConfig={reportSort}
                  onSortChange={sortReportBy}
                  timeZone={timeZone}
                  t={context.t}
                  projectTimeZone={
                    currentProject?.TimezoneName ||
                    currentProject?.TimeZoneName ||
                    currentProject?.timezone
                  }
                />
              </Box>
            </Paper>
          </Resizable>
        </div>
      </Draggable>
    );
  }

  const modalStyle = isMaximized
    ? {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      '--maintenance-fixed-column-width': `${getFixedColumnWidth()}px`,
      '--maintenance-top-row-height': `${DASHBOARD_TOP_ROW_HEIGHT}px`,
      '--maintenance-bottom-row-min-height': `${DASHBOARD_BOTTOM_ROW_HEIGHT}px`,
    }
    : {
      width: size.width,
      '--maintenance-dashboard-window-height': `${size.height}px`,
      '--maintenance-fixed-column-width': `${getFixedColumnWidth()}px`,
      '--maintenance-top-row-height': `${DASHBOARD_TOP_ROW_HEIGHT}px`,
      '--maintenance-bottom-row-min-height': `${DASHBOARD_BOTTOM_ROW_HEIGHT}px`,
    };

  const content = (
    <Paper
      className={`dashboard-modal maintenance-dashboard-modal ${isMaximized ? 'maximized' : ''
        } ${hasBeenResized ? 'maintenance-dashboard-resized' : ''}`}
      style={modalStyle}
      ref={modalRef}
      dir='ltr'
    >
      <DashboardHeader
        onClose={onClose}
        onMinimize={toggleMinimize}
        onMaximize={toggleMaximize}
        isMaximized={isMaximized}
        title={context.t('maintenance_dashboard') || 'Maintenance Dashboard'}
        showMinimize
        showMaximize
        t={context.t}
      />
      <div className='dashboard-modal-content maintenance-dashboard-content'>
        <Box className='maintenance-dashboard-view'>
          {/* {(maintenanceDashboard?.loading ||
            maintenanceReportSensors?.loading) && (
              <Typography className='maintenance-dashboard-loading'>
                {context.t('loading_data')}
              </Typography>
            )} */}

          {currentProject && (
            <Grid
              container
              spacing={1.5}
              className='maintenance-dashboard-grid'
            >
              <Grid item xs={12} md={4} className='maintenance-logo-grid-item'>
                <ProjectNameLogoBlock
                  projectName={
                    currentProject.dashboard_name || currentProject.ShortName
                  }
                  customerLogoUrl={customerLogo?.data}
                  t={context.t}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={8}
                className='maintenance-required-grid-item'
              >
                <RequiredMaintenanceBlock
                  totalFaulty={totalFaultyDevices}
                  items={requiredMaintenanceItems}
                  t={context.t}
                  isHebrew={isHebrew}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
                className='maintenance-system-grid-item'
              >
                <SystemStatusBlock
                  items={[
                    {
                      key: 'audio',
                      label: context.t('audio') || 'Audio',
                      value: Number(maintenanceData.audio_problems) || 0,
                    },
                    {
                      key: 'fm_reception',
                      label: context.t('fm_reception') || 'FM Reception',
                      value: Number(maintenanceData.fm_reception_problems) || 0,
                    },
                    {
                      key: 'no_fm',
                      label: context.t('no_fm') || 'No FM',
                      value: Number(maintenanceData.radio_zero) || 0,
                    },
                    {
                      key: 'low_battery',
                      label: context.t('low_battery') || 'Low Battery',
                      value: Number(maintenanceData.battery) || 0,
                    },
                    {
                      key: 'inactive',
                      label: context.t('inactive') || 'Inactive',
                      value: sensorData.inactive,
                    },
                  ]}
                  installedSensors={sensorData.total}
                  t={context.t}
                  isHebrew={isHebrew}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={8}
                className='maintenance-report-grid-item'
              >
                <ReportOverviewBlock
                  onDownload={downloadMaintenanceReport}
                  downloading={isDownloadingReport}
                  reportSensors={reportSensors}
                  error={maintenanceReportSensors?.error}
                  onSensorClick={handleReportSensorClick}
                  selectedSensorId={selectedSensorId}
                  sortConfig={reportSort}
                  onSortChange={sortReportBy}
                  timeZone={timeZone}
                  t={context.t}
                  projectTimeZone={
                    currentProject?.TimezoneName ||
                    currentProject?.TimeZoneName ||
                    currentProject?.timezone
                  }
                />
              </Grid>
            </Grid>
          )}
        </Box>
      </div>
      <PopupAlert {...popupAlert} />
    </Paper>
  );

  if (isMaximized) return content;

  return (
    <div className='dashboard-modal-overlay maintenance-dashboard-overlay'>
      <Draggable
        handle='.dashboard-header'
        position={position}
        onDrag={(_, data) => setPosition({ x: data.x, y: data.y })}
        bounds='parent'
      >
        <Resizable
          width={size.width}
          height={size.height}
          onResizeStart={startResize}
          onResize={resizeWindow}
          onResizeStop={stopResize}
          minConstraints={[
            Math.min(DASHBOARD_MIN_WIDTH, Math.round(window.innerWidth * 0.9)),
            Math.min(
              DASHBOARD_MIN_HEIGHT,
              Math.round(window.innerHeight * 0.8)
            ),
          ]}
          maxConstraints={[
            Math.round(window.innerWidth * 0.98),
            window.innerHeight * 0.98,
          ]}
          resizeHandles={['se']}
        >
          {content}
        </Resizable>
      </Draggable>
    </div>
  );
};

MaintenanceDashboardModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

MaintenanceDashboardModal.contextTypes = {
  t: PropTypes.func.isRequired,
};

export default MaintenanceDashboardModal;
